import { useRef, forwardRef, useEffect } from "react";

import { ChevronRightIcon } from "@heroicons/react/24/solid";

import { useSolanaTransfer } from "../Contexts/useSolanaTransfer";
import { Copyable } from "./Copyable";

import solscan_icon from "../assets/Solscan Icon.svg";
import solana_round_square_icon from "../assets/Solana Round Square Icon.svg";
import cold_wallet_round_square_icon from "../assets/Cold Wallet Round Square Icon.svg";
import polygon_round_square_icon from "../assets/Polygon Round Square Icon.svg";

const NFTS_IMAGE_URL_PREFIX = process.env.REACT_APP_NFTS_IMAGE_URL_PREFIX;
const SOLSCAN_URL_PREFIX = process.env.REACT_APP_SOLSCAN_URL_PREFIX;
const DISCORD_URL = process.env.REACT_APP_SUPPORT_URL;

export function NewSwappingResultPage({
    closeNewSwappingResultPage = () => {},
}) {
    const titleRef = useRef(null);
    const { transferResult } = useSolanaTransfer();
    console.log("New Swapping Result:", transferResult);

    useEffect(() => {
        titleRef.current.scrollIntoView(true);
    }, []);

    return (
        <main className="w-full grow">
            <div className="flex h-full flex-col justify-start px-1 pb-6 md:px-6">
                <PageTitle ref={titleRef} />
                <div className="flex grow items-start">
                    <div className="mt-12 w-full rounded-lg border border-gray-100 bg-white shadow xl:mt-14 2xl:mt-24">
                        <TransferResultTable
                            transferResult={transferResult}
                            closeNewSwappingResultPage={
                                closeNewSwappingResultPage
                            }
                        />
                    </div>
                </div>
            </div>
        </main>
    );
}

const PageTitle = forwardRef(function PageTitle(props, ref) {
    return (
        <div
            ref={ref}
            className="flex flex-wrap items-start justify-between gap-6 px-5 pt-12 md:px-0 md:pt-16"
        >
            <div className="flex flex-col items-start gap-4">
                <span className="text-base font-bold text-orange-600">
                    Congratulations!
                </span>
                <span className="font-burbank text-2xl font-semibold text-gray-800 sm:text-4xl">
                    Swap Request Received
                </span>
                <span className="text-xs font-medium text-gray-500 sm:text-base">
                    We have received your request to swap your Neocollectible.
                    Please wait up to 48 hours for your request to be processed.
                </span>
            </div>
        </div>
    );
});

function TransferResultTable({ transferResult, closeNewSwappingResultPage }) {
    return (
        <div className="flex flex-col">
            <div className="flex w-full flex-col items-center justify-center border-b border-gray-100 px-3 py-3 pb-4 sm:flex-nowrap md:px-6 md:pb-8 md:pt-6">
                <h3 className="py-3 text-center text-base font-bold text-gray-800 md:py-6 md:text-lg">
                    Your Neocollectible is on the way!
                </h3>
                <div className="flex items-center justify-center gap-x-4 md:gap-x-9">
                    <img
                        className="h-16 w-16"
                        src={solana_round_square_icon}
                        alt="Solana Wallet"
                        draggable="false"
                    />
                    <div className="flex gap-0">
                        <ChevronRightIcon className="h-3 w-3 animate-pulse text-gray-400" />
                        <ChevronRightIcon className="h-3 w-3 animate-pulse text-gray-500" />
                        <ChevronRightIcon className="h-3 w-3 animate-pulse text-gray-600" />
                    </div>
                    <img
                        className="h-16 w-16"
                        src={cold_wallet_round_square_icon}
                        alt="Cold Wallet"
                        draggable="false"
                    />
                    <div className="flex gap-0">
                        <ChevronRightIcon className="h-3 w-3 animate-pulse text-gray-400" />
                        <ChevronRightIcon className="h-3 w-3 animate-pulse text-gray-500" />
                        <ChevronRightIcon className="h-3 w-3 animate-pulse text-gray-600" />
                    </div>
                    <img
                        className="h-16 w-16"
                        src={polygon_round_square_icon}
                        alt="Polygon Wallet"
                        draggable="false"
                    />
                </div>
            </div>
            <div className="flex w-full flex-wrap items-center justify-between border-b border-gray-100 px-3 py-3 sm:flex-nowrap md:px-6">
                <h3 className="text-base font-bold text-gray-800 md:text-lg">
                    Swap Details
                </h3>
                <div className="flex-shrink-0">
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            closeNewSwappingResultPage();
                        }}
                        type="button"
                        className="relative inline-flex cursor-pointer items-center px-2 text-sm font-medium text-orange-600 transition duration-200 hover:text-orange-700"
                    >
                        View Swap History
                    </button>
                </div>
            </div>
            <table className="w-full divide-y divide-gray-100">
                <thead>
                    <tr className="bg-gray-50">
                        <th
                            scope="col"
                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-400 md:pl-6"
                        >
                            Item
                        </th>
                        <th
                            scope="col"
                            className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-400 lg:table-cell"
                        >
                            Transaction
                        </th>
                        <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-400"
                        >
                            Status
                        </th>
                        <th
                            scope="col"
                            className="relative hidden py-3.5 pl-3 pr-4 md:pr-6 lg:table-cell"
                        >
                            <span className="sr-only">Remarks</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {transferResult.map((record) => (
                        <tr key={record.NFTTokenAddr}>
                            <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
                                <div className="flex items-center">
                                    <div className="hidden h-16 w-16 flex-shrink-0 lg:block">
                                        <picture>
                                            <source
                                                type="image/avif"
                                                srcSet={`${NFTS_IMAGE_URL_PREFIX}100x100/${record.NFTTokenAddr}.avif 100w, ${NFTS_IMAGE_URL_PREFIX}200x200/${record.NFTTokenAddr}.avif 200w`}
                                                width="64"
                                                height="64"
                                            />
                                            <source
                                                type="image/webp"
                                                srcSet={`${NFTS_IMAGE_URL_PREFIX}100x100/${record.NFTTokenAddr}.webp 100w, ${NFTS_IMAGE_URL_PREFIX}200x200/${record.NFTTokenAddr}.webp 200w`}
                                                width="64"
                                                height="64"
                                            />
                                            <img
                                                draggable="false"
                                                className="h-16 w-16 rounded"
                                                loading="lazy"
                                                src={`${NFTS_IMAGE_URL_PREFIX}100x100/${record.NFTTokenAddr}.png`}
                                                srcSet={`${NFTS_IMAGE_URL_PREFIX}200x200/${record.NFTTokenAddr}.png 200w`}
                                                alt={record.NFTName}
                                                width="64"
                                                height="64"
                                            />
                                        </picture>
                                    </div>
                                    <div className="ml-0 lg:ml-4">
                                        <div className="font-medium text-gray-800">
                                            {record.NFTName}
                                        </div>
                                    </div>
                                </div>
                                <dl className="font-normal lg:hidden">
                                    <dt className="sr-only">Transaction</dt>
                                    <dd className="mt-1 truncate text-gray-700">
                                        <div className="inline-flex items-center justify-start">
                                            <Copyable
                                                value={record.SolanaTxSig}
                                            >
                                                {record.SolanaTxSig.slice(
                                                    0,
                                                    4
                                                ) +
                                                    "..." +
                                                    record.SolanaTxSig.slice(
                                                        -4
                                                    )}
                                            </Copyable>
                                            {record.Status !== "failed" && (
                                                <a
                                                    className="ml-0.5"
                                                    target="_blank"
                                                    href={`${SOLSCAN_URL_PREFIX}${record.SolanaTxSig}/`}
                                                    rel="noreferrer noopener"
                                                >
                                                    <img
                                                        src={solscan_icon}
                                                        alt="Solscan"
                                                        className="h-4 w-4"
                                                    />
                                                </a>
                                            )}
                                        </div>
                                    </dd>
                                    {record.Status === "failed" && (
                                        <>
                                            <dt className="sr-only sm:hidden">
                                                Remarks
                                            </dt>
                                            <dd className="mt-1 font-normal text-gray-500 lg:hidden">
                                                {" "}
                                                Please try swapping again.
                                                Contact us on{" "}
                                                <a
                                                    href={DISCORD_URL}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="font-bold text-orange-600 hover:text-orange-800"
                                                >
                                                    Discord
                                                </a>{" "}
                                                for further assistance.
                                            </dd>
                                        </>
                                    )}
                                </dl>
                            </td>
                            <td className="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell">
                                <div className="inline-flex items-center justify-start">
                                    <Copyable value={record.SolanaTxSig}>
                                        {record.SolanaTxSig.slice(0, 4) +
                                            "..." +
                                            record.SolanaTxSig.slice(-4)}
                                    </Copyable>
                                    {record.Status !== "failed" && (
                                        <a
                                            className="ml-0.5"
                                            target="_blank"
                                            href={`${SOLSCAN_URL_PREFIX}${record.SolanaTxSig}/`}
                                            rel="noreferrer noopener"
                                        >
                                            <img
                                                src={solscan_icon}
                                                alt="Solscan"
                                                className="h-4 w-4"
                                            />
                                        </a>
                                    )}
                                </div>
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500">
                                <StatusLabel status={record.Status} />
                            </td>
                            <td className="hidden max-w-xs py-4 pl-3 pr-4 text-left text-sm font-normal sm:pr-6 lg:table-cell">
                                {record.Status === "failed" && (
                                    <>
                                        {" "}
                                        Please try swapping again. Contact us on{" "}
                                        <a
                                            href={DISCORD_URL}
                                            rel="noreferrer noopener"
                                            className="font-bold text-orange-600 hover:text-orange-800"
                                        >
                                            Discord
                                        </a>{" "}
                                        for further assistance.
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function StatusLabel({ status }) {
    const processingStatus = ["sent", "confirmed", "processing"];

    switch (status) {
        case "finalized":
            return (
                <span className="inline-flex items-center rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-800">
                    Success
                </span>
            );
        case "failed":
            return (
                <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-600">
                    Failed
                </span>
            );
        default:
            if (processingStatus.indexOf(status) !== -1) {
                return (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-600">
                        Processing
                    </span>
                );
            } else {
                return <></>;
            }
    }
}
